import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut } from "lucide-react";
import axios from "axios";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { TOKEN_KEY as DEVADMIN_TOKEN_KEY } from "@/lib/devadminApi";
import {
  MEMBER_TOKEN_KEY, ADMIN_TOKEN_KEY,
} from "@/lib/authApi";

const LEADER_KEY = "bni-leader";

function readIdentity() {
  // Priority: member > admin > devadmin > leader (whichever token is present)
  if (typeof window === "undefined") return null;
  if (localStorage.getItem(MEMBER_TOKEN_KEY)) return { kind: "member" };
  if (localStorage.getItem(ADMIN_TOKEN_KEY)) return { kind: "admin" };
  if (localStorage.getItem(DEVADMIN_TOKEN_KEY)) return { kind: "devadmin" };
  try {
    const l = JSON.parse(localStorage.getItem(LEADER_KEY) || "null");
    if (l?.name) return { kind: "leader", name: l.name, subtitle: `Table leader${l.table ? ` · Table #${l.table}` : ""}` };
  } catch {}
  return null;
}

function initialsOf(name) {
  return (name || "").split(/\s+/).filter(Boolean).slice(0, 2).map(w => w[0].toUpperCase()).join("") || "·";
}

export function TopNav() {
  const nav = useNavigate();
  const [identity, setIdentity] = useState(() => readIdentity());
  const [profile, setProfile] = useState(null); // { name, subtitle, photo }

  // Re-check identity on route changes (localStorage can change after login/logout)
  useEffect(() => {
    const sync = () => setIdentity(readIdentity());
    window.addEventListener("storage", sync);
    const iv = setInterval(sync, 1500);
    return () => { window.removeEventListener("storage", sync); clearInterval(iv); };
  }, []);

  // Load live profile (member fetches /api/member/me; admin/devadmin we show role label only)
  useEffect(() => {
    let aborted = false;
    const load = async () => {
      if (!identity) { setProfile(null); return; }
      if (identity.kind === "member") {
        try {
          const t = localStorage.getItem(MEMBER_TOKEN_KEY);
          const r = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/member/me`, { headers: { Authorization: `Bearer ${t}` } });
          if (aborted) return;
          const reg = r.data?.registration || {};
          const photo = reg.photo_url
            ? (reg.photo_url.startsWith("/api") ? `${process.env.REACT_APP_BACKEND_URL}${reg.photo_url}?v=${encodeURIComponent(reg.photo_at || "")}` : reg.photo_url)
            : null;
          setProfile({ name: reg.name || "Member", subtitle: reg.chapter || "—", photo });
        } catch { if (!aborted) setProfile({ name: "Member", subtitle: "—", photo: null }); }
      } else if (identity.kind === "admin") {
        setProfile({ name: "Admin", subtitle: "Admin · Davangere", photo: null });
      } else if (identity.kind === "devadmin") {
        setProfile({ name: "DevAdmin", subtitle: "DevAdmin · Platform", photo: null });
      } else if (identity.kind === "leader") {
        setProfile({ name: identity.name, subtitle: identity.subtitle || "Table leader", photo: null });
      }
    };
    load();
    return () => { aborted = true; };
  }, [identity]);

  const logout = () => {
    localStorage.removeItem(MEMBER_TOKEN_KEY);
    localStorage.removeItem(ADMIN_TOKEN_KEY);
    localStorage.removeItem(DEVADMIN_TOKEN_KEY);
    localStorage.removeItem(LEADER_KEY);
    setIdentity(null); setProfile(null);
    nav("/register");
  };

  const avatarInitials = initialsOf(profile?.name);

  return (
    <div className="sticky top-0 z-50 bg-[#0B0D10]/95 backdrop-blur-md hairline-b">
      <div className="max-w-[1400px] mx-auto flex items-center justify-between px-4 sm:px-6 py-3">
        <button
          data-testid="brand-home-btn"
          onClick={() => nav("/")}
          className="flex items-center gap-3 text-left min-w-0"
        >
          <img
            src="/risse-bni-text.png"
            alt="RISSE 26 · BNI Davangere Members Day"
            className="hidden sm:block h-9 w-auto object-contain select-none"
            draggable="false"
            style={{ maxWidth: "40vw", filter: "drop-shadow(0 2px 10px rgba(0,0,0,0.55))" }}
          />
          <span className="hidden sm:inline-block w-px h-10" style={{ background: "rgba(247,244,238,0.16)" }} />
          <img
            src="/risse-bni-logo.png"
            alt="RISSE 26 landmarks"
            className="h-14 sm:h-16 w-auto select-none"
            draggable="false"
            style={{ imageRendering: "auto", filter: "drop-shadow(0 2px 10px rgba(0,0,0,0.55))" }}
          />
        </button>

        {identity ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid="profile-avatar-btn"
                className="relative flex items-center justify-center rounded-full overflow-hidden focus:outline-none transition-shadow"
                style={{
                  width: 40, height: 40,
                  border: "1px solid var(--hairline)",
                  background: "var(--panel-2)",
                  boxShadow: "0 0 0 2px rgba(11,13,16,0.6), 0 0 0 3px transparent",
                }}
                aria-label="Open profile menu"
              >
                {profile?.photo ? (
                  <img src={profile.photo} alt={profile.name} className="w-full h-full object-cover" />
                ) : (
                  <span className="font-serif text-sm" style={{ color: "var(--red-2)" }}>{avatarInitials}</span>
                )}
                <span
                  className="absolute bottom-0 right-0 w-2.5 h-2.5 rounded-full border-2"
                  style={{ background: "var(--green)", borderColor: "#0B0D10" }}
                />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              sideOffset={10}
              className="!bg-[var(--panel)] !border-[var(--hairline)] !rounded-none min-w-[240px] !p-0"
            >
              <DropdownMenuLabel className="px-3 py-3 !font-normal">
                <div className="flex items-center gap-3">
                  {profile?.photo ? (
                    <img src={profile.photo} alt="" className="w-9 h-9 object-cover border border-[var(--hairline)]" />
                  ) : (
                    <div className="w-9 h-9 border border-[var(--hairline)] bg-[var(--panel-2)] flex items-center justify-center font-serif text-xs" style={{ color: "var(--red-2)" }}>{avatarInitials}</div>
                  )}
                  <div className="min-w-0">
                    <div className="text-sm truncate" style={{ color: "var(--text)" }}>{profile?.name || "Signed in"}</div>
                    <div className="mono-label truncate" style={{ color: "var(--taupe)" }}>{profile?.subtitle || ""}</div>
                  </div>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator className="!bg-[var(--hairline)] !my-0" />
              <DropdownMenuItem
                data-testid="logout-menu-item"
                onClick={logout}
                className="!rounded-none !px-3 !py-2.5 flex items-center gap-3 cursor-pointer hover:!bg-[var(--panel-2)] focus:!bg-[var(--panel-2)]"
              >
                <LogOut size={14} style={{ color: "var(--red)" }} />
                <span className="text-sm">Logout</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ) : null}
      </div>
    </div>
  );
}
